import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { SITE_URL } from "@/lib/seo";
import api from "@/lib/api";
import FlagSelect from "@/components/FlagSelect";
import { SCHENGEN_CODES } from "@/lib/worldCountries";
import { findDedicatedRoute } from "@/lib/spainUsaNationalityConfig";
import { ArrowRight, MapPin, Search } from "lucide-react";
import "flag-icons/css/flag-icons.min.css";

export default function GuideHub() {
  const [destinations, setDestinations] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [residenceCountries, setResidenceCountries] = useState([]);
  const [nat, setNat] = useState("");
  const [residence, setResidence] = useState("");
  const [residenceTouched, setResidenceTouched] = useState(false);
  const [dest, setDest] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/public/guide"),
      api.get("/public/guide/nationalities"),
      api.get("/public/countries").catch(() => ({ data: { countries: [] } })),
    ]).then(([d, n, c]) => {
      setDestinations(d.data.destinations || []);
      setNationalities(n.data.nationalities || []);
      // Residence uses the full ISO-3166 list. Fallback to nationalities if
      // the endpoint is unavailable (older backend).
      const countries = c.data.countries || [];
      setResidenceCountries(countries.length ? countries : (n.data.nationalities || []));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Default "Applying from" to the nationality country until the user
  // explicitly picks a different residence.
  useEffect(() => {
    if (nat && !residenceTouched) setResidence(nat);
  }, [nat, residenceTouched]);

  const canGo = dest && nat;
  const gotoGuide = () => {
    if (!dest) return;
    // Check if we have a dedicated nationality landing page for this exact
    // (nationality + residence + destination) combination. If yes, route
    // there instead of the generic combination page. Adding a config entry
    // = auto-wired routing, no code change here.
    const dedicated = findDedicatedRoute({
      nationalityCode: nat,
      residenceCode: residence,
      destinationCode: dest,
    });
    if (dedicated) {
      navigate(dedicated);
      return;
    }
    const destObj = destinations.find((d) => d.code === dest);
    const natObj = nationalities.find((n) => n.code === nat);
    const destSeg = destObj?.slug || dest.toLowerCase();
    const q = residence ? `?residence=${residence}` : "";
    if (natObj) navigate(`/schengen-visa-guide/${destSeg}/from-${natObj.slug || nat.toLowerCase()}${q}`);
    else navigate(`/schengen-visa-guide/${destSeg}${q}`);
  };

  return (
    <div className="min-h-screen bg-white text-[#0f1b2d]" data-testid="guide-hub-page">
      <SEO
        title="Schengen Visa Guide — Requirements, Fees & Documents by Country | SchengenFile"
        description="Interactive Schengen visa guide: choose your nationality and destination to see required documents, fees, processing times and where to apply."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Schengen Visa Guide", item: `${SITE_URL}/schengen-visa-guide` },
          ],
        }}
      />
      <Header />
      <main>
        <section className="relative py-16 sm:py-24 bg-[#0f1b2d] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0f1b2d] via-[#0f1b2d] to-[#1e3a8a]/70" />
          <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center text-white">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[#60a5fa]">Interactive Guide</div>
            <h1 className="mt-3 font-display font-bold text-4xl sm:text-5xl lg:text-6xl">Schengen Visa Guide</h1>
            <p className="mt-4 text-base sm:text-lg text-white/75 max-w-2xl mx-auto">
              Pick your nationality and your Schengen destination. We'll show you exactly what documents you need, how much it costs, and how long it takes.
            </p>

            <div className="mt-10 bg-white text-[#0f1b2d] rounded-xl p-4 sm:p-6 shadow-2xl max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-3 text-left" data-testid="guide-hub-selector">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#64748b]">What is your nationality?</label>
                <div className="mt-1">
                  <FlagSelect
                    testId="guide-hub-nationality"
                    ariaLabel="What is your nationality?"
                    placeholder="Select nationality"
                    value={nat}
                    onChange={setNat}
                    options={nationalities}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#64748b]">Applying from?</label>
                <div className="mt-1">
                  <FlagSelect
                    testId="guide-hub-residence"
                    ariaLabel="Applying from"
                    placeholder="Country of residence"
                    value={residence}
                    onChange={(v) => { setResidence(v); setResidenceTouched(true); }}
                    options={residenceCountries.filter((c) => !SCHENGEN_CODES.has(c.code))}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#64748b]">Travelling to?</label>
                <div className="mt-1">
                  <FlagSelect
                    testId="guide-hub-destination"
                    ariaLabel="Travelling to"
                    placeholder="Select destination"
                    value={dest}
                    onChange={setDest}
                    options={destinations}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={gotoGuide}
                disabled={!dest}
                data-testid="guide-hub-go"
                className="sm:col-span-3 inline-flex items-center justify-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-40 text-white font-semibold py-3 rounded-md transition"
              >
                {canGo ? "View my Schengen guide" : "Choose a destination to continue"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="flex items-end justify-between gap-4 mb-8">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#2563eb] font-semibold">Browse by country</div>
                <h2 className="mt-1 font-display font-bold text-2xl sm:text-3xl">Schengen destinations</h2>
              </div>
              <div className="hidden sm:block text-sm text-[#64748b]">{destinations.length} countries covered</div>
            </div>
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-32 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="guide-hub-destination-grid">
                {destinations.map((d) => (
                  <Link
                    key={d.code}
                    to={`/schengen-visa-guide/${d.slug || d.code.toLowerCase()}`}
                    data-testid={`guide-hub-dest-${d.code.toLowerCase()}`}
                    className="group rounded-xl border-2 border-[#e2e8f0] bg-white p-5 hover:border-[#2563eb] hover:-translate-y-0.5 transition-all"
                  >
                    <span className={`fi fi-${(d.flag_code || d.code).toLowerCase()} inline-block !w-10 !h-8 rounded shadow-sm`} aria-hidden />
                    <div className="mt-3 font-display font-bold text-[#0f1b2d]">{d.name}</div>
                    <div className="mt-1 text-xs text-[#2563eb] font-semibold inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                      View guide <ArrowRight className="w-3 h-3" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="py-14 bg-[#f8fafc] border-t border-[#e2e8f0]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h3 className="font-display font-bold text-2xl sm:text-3xl text-[#0f1b2d]">
              Need help preparing your file?
            </h3>
            <p className="mt-2 text-[#475569] max-w-2xl mx-auto">
              Once you know what documents you need, SchengenFile prepares consulate-ready flight PNRs, hotel bookings, cover letters and insurance certificates in 24 hours.
            </p>
            <Link
              to="/pricing"
              data-testid="guide-hub-cta-pricing"
              className="mt-6 inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-6 py-3 rounded-md transition"
            >
              See our packages <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
